import { studyQueryKeys } from '@/config/queryKeys';
import { STUDIES_QUERY_GC_TIME, STUDIES_QUERY_STALE_TIME, mainIconSize } from '@/constants.ts';
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect';
import { agGridDatasourceQueryKey } from '@/lib/createAgGridDatasource';
import { StudiesContent } from '@/pages/Studies/components';
import CreateForm from '@/pages/Studies/components/CreateForm.tsx';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app.ts';
import type { PaginatedStudiesResponse } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Dialog } from '@nautilusbio/nb-design-system/Dialog';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useSearch } from '@tanstack/react-router';
import { CloudDownload, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import styles from '../../pages/Studies/styles.module.css';

const studiesSearchSchema = z.object({
  openCreate: z.boolean().optional(),
});

export const Route = createFileRoute('/studies/')({
  beforeLoad: ({ context }) => {
    // Redirect to log in if user is not authenticated
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({
        to: '/login',
      });
    }
  },
  component: RouteComponent,
  validateSearch: studiesSearchSchema,
});

function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div role="alert" aria-describedby="error-heading error-message">
      <Text as="h2" id="error-heading">
        Something went wrong:
      </Text>
      <Text as="pre" id="error-message">
        {error.message}
      </Text>
      <Button aria-label="Retry loading studies" data-testid="error-refresh-btn" onPress={resetErrorBoundary}>
        Try again
      </Button>
    </div>
  );
}

function RouteComponent() {
  usePostLoginRedirect();
  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const search = useSearch({ from: '/studies/' });
  const userOrg = useAppStore(useShallow(state => state.selectedOrganization));

  // Handle study creation success - invalidate caches and close dialog
  const handleStudyCreated = useCallback(() => {
    setCreateOpen(false);

    if (userOrg?.id) {
      // Invalidate route-level query cache
      void queryClient.invalidateQueries({
        queryKey: studyQueryKeys.listSearch(userOrg.id),
      });

      // Invalidate AG Grid datasource cache so the table fetches fresh
      // data when the user navigates back to the studies list
      void queryClient.invalidateQueries({
        queryKey: agGridDatasourceQueryKey(studyQueryKeys.endpoints.listSearch(userOrg.id)),
      });
    }
  }, [userOrg?.id, queryClient]);

  /**
   * Fetch studies data using React Query
   *
   * - Fetches all studies for both card and table view
   * - React Query automatically caches and updates data
   * - Mutations update this cache automatically
   * - Table view uses server-side sorting/filtering (see queryFn parameters)
   * - staleTime: 30 minutes - data is considered fresh for 30 minutes, no refetch on navigation
   * - gcTime: 60 minutes - cached data is kept in memory for 60 minutes after last use
   */
  const queryKey = userOrg ? studyQueryKeys.listSearch(userOrg.id) : ['disabled-studies-query'];

  const queryFn = async (): Promise<PaginatedStudiesResponse> => {
    if (!userOrg) {
      throw new Error('Organization ID is required');
    }

    // Fetch all studies with default sorting
    return searchStudies(userOrg?.id, {
      skip: 0,
      limit: 500, // API maximum limit - orgs with 500+ studies will only see most recent 500
      sort_by: 'created_at',
      sort_order: 'desc',
    });
  };

  const {
    data: studies,
    error,
    isLoading,
    refetch,
  } = useQuery<PaginatedStudiesResponse, Error>({
    queryKey,
    queryFn,
    enabled: !!userOrg,
    gcTime: STUDIES_QUERY_GC_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: 'always',
    refetchOnMount: 'always',
    retry: 2,
    staleTime: STUDIES_QUERY_STALE_TIME,
  });

  // Dialog open state
  const [createIsOpen, setCreateOpen] = useState(false);

  // Live count of rows matching the grid's current filter state — kept in sync
  // via createAgGridDatasource's onTotalChanged, since `studies.total` above
  // reflects the unfiltered "load everything" query, not the grid's filters.
  const [filteredTotal, setFilteredTotal] = useState<number | null>(null);

  // Reset to the unfiltered total whenever the org changes, so a stale count
  // from the previous org's filtered grid doesn't briefly show.
  useEffect(() => {
    setFilteredTotal(null);
  }, [userOrg?.id]);

  // Open dialog if openCreate flag is present in URL
  useEffect(() => {
    if (search.openCreate) {
      setCreateOpen(true);
    }
  }, [search.openCreate]);

  if (error) {
    return <ErrorFallback error={error} resetErrorBoundary={refetch} />;
  }

  const createStudyTxt = t('study.create', { ns: 'create' });

  return (
    <Stack alignItems="start" className={styles.container} orientation="vertical">
      {!!userOrg && (
        <Stack
          alignItems="center"
          className={styles.newTitleRow}
          justifyContent="space-between"
          orientation="horizontal"
        >
          <Stack alignItems="start" justifyContent="start" orientation="vertical">
            <Text as="h1" className={styles.title}>
              {t('page_title', { ns: 'studies' })}
            </Text>
            {!isLoading && studies && (
              <Text as="small" className={styles.subtitle} data-testid="studies-count">
                {t('study_count', { ns: 'studies', count: filteredTotal ?? studies.total })}
              </Text>
            )}
          </Stack>

          <Stack alignItems="center" orientation="horizontal" gap={2}>
            <Tooltip
              content={t('download_list_coming_soon', { ns: 'studies' })}
              placement="bottom"
              trigger={
                <Button data-testid="studies-download-list-btn" isDisabled size="md" visual="unstyled">
                  <CloudDownload size={mainIconSize} />
                  {t('download_list', { ns: 'studies' })}
                </Button>
              }
            />

            <Dialog
              content={<CreateForm onSuccess={handleStudyCreated} userOrg={userOrg?.id} />}
              data-testid={'create-new-study-btn'}
              floatingProps={{
                open: createIsOpen,
                onOpenChange: setCreateOpen,
              }}
              onOpenChange={setCreateOpen}
              title={createStudyTxt}
              triggerProps={{
                children: (
                  <>
                    <Plus size={mainIconSize} />
                    {createStudyTxt}
                  </>
                ),
                size: 'md',
                visual: 'unstyled',
              }}
            />
          </Stack>
        </Stack>
      )}

      {userOrg && (
        <StudiesContent isLoading={isLoading} onFilteredTotalChanged={setFilteredTotal} studies={studies || null} />
      )}
    </Stack>
  );
}
